import type { z } from "zod";

import {
  parseListResponse,
  parseNullableResponse,
} from "@/state/api/response.types";
import {
  managementRoleSchema,
  permissionSchema,
} from "@/state/api/roles.types";
import { authUserSchema } from "@/state/api/users.types";

import type { ResponseDto } from "./authTypes";

/**
 * auth-service's answers, read with the schemas both apps share
 * (`state/api/{roles,users}.types.ts`, DEDUPE-2 B6). A list drops a row of
 * another shape and reports it, where these used to throw on the first one; a
 * single record of another shape still fails its request.
 */

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/** One record (a write's answer): `{ data }`, parsed strictly. */
export function parseResponse<TItem>(
  response: unknown,
  parseItem: (item: unknown) => TItem
): ResponseDto<TItem> {
  return { data: parseItem(isRecord(response) ? response["data"] : undefined) };
}

const strictly =
  <TSchema extends z.ZodTypeAny>(schema: TSchema) =>
  (value: unknown): z.output<TSchema> =>
    schema.parse(value);

export const parseManagementRole = strictly(managementRoleSchema);

export const parsePermissionListResponse = (response: unknown) =>
  parseListResponse(response, permissionSchema, "getPermissions");

export const parseManagementRoleListResponse = (response: unknown) =>
  parseListResponse(response, managementRoleSchema, "getManagementRoles");

export const parseAuthUserListResponse = (response: unknown) =>
  parseListResponse(response, authUserSchema, "getObjectUsers");

export const parseAuthUserResponse = (response: unknown) =>
  parseResponse(response, strictly(authUserSchema));

export const parseNullableAuthUserResponse = (response: unknown) =>
  parseNullableResponse(response, authUserSchema, "updateUser");
